import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, Share2, Sparkles } from 'lucide-react';
import { toPng } from 'html-to-image';
import { BACKOFFICE_URL } from './AccessGate';
import { useLanguage } from '../hooks/useLanguage';

/**
 * Pokémon / NFT-style collectible card showing the user simulation.
 * Rarity is derived from the selected profile:
 *  - conservative → Bronze
 *  - balanced     → Silver / Cyan
 *  - exotic       → Gold / Magenta-Violet
 *  - custom       → Prismatic (rainbow)
 */
const RARITY = {
  conservative: {
    label: 'COMMON',
    tint: '#b87333', // bronze
    tint2: '#e8a87c',
    glow: 'rgba(184,115,51,0.55)',
    frame: 'linear-gradient(135deg, #b87333 0%, #e8a87c 50%, #b87333 100%)',
  },
  balanced: {
    label: 'RARE',
    tint: '#00f4ff',
    tint2: '#12b7ff',
    glow: 'rgba(0,244,255,0.55)',
    frame: 'linear-gradient(135deg, #00f4ff 0%, #12b7ff 50%, #7c3aed 100%)',
  },
  exotic: {
    label: 'LEGENDARY',
    tint: '#ffd700',
    tint2: '#ff00aa',
    glow: 'rgba(255,215,0,0.6)',
    frame: 'linear-gradient(135deg, #ffd700 0%, #ff00aa 50%, #7c3aed 100%)',
  },
  custom: {
    label: 'MYTHIC',
    tint: '#ff00aa',
    tint2: '#7c3aed',
    glow: 'rgba(255,0,170,0.55)',
    frame:
      'linear-gradient(135deg, #00f4ff 0%, #ff00aa 33%, #ffd700 66%, #7c3aed 100%)',
  },
};

const formatCurrency = (amount) => {
  if (!isFinite(amount)) return '$0';
  if (Math.abs(amount) >= 1e6) return `$${(amount / 1e6).toFixed(2)}M`;
  if (Math.abs(amount) >= 1e3) return `$${(amount / 1e3).toFixed(1)}K`;
  return `$${Math.round(amount)}`;
};

const PortfolioCard = ({
  profile,
  totalCapital,
  monthlyContribution,
  allocations,
  aiSplit,
  projections,
}) => {
  const { t, currentLang } = useLanguage();
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0, scale: 1 });
  const [sheenPos, setSheenPos] = useState({ x: 50, y: 50 });
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareNotice, setShareNotice] = useState(false);

  const profileKey = profile && RARITY[profile] ? profile : 'custom';
  const rarity = RARITY[profileKey];

  // Longest horizon projection (5y)
  const p5y = projections[1825] || {};
  const fv = p5y.total || 0;
  const roi = p5y.roi || 0;
  const totalInvested = p5y.totalInvested || totalCapital;

  // Generate a pseudo-unique serial number based on config
  const serial = Math.abs(
    (
      Math.round(totalCapital * 7) +
      Math.round(monthlyContribution * 13) +
      Math.round((allocations.dcaCrypto || allocations.bitcoin || 0) * 23) +
      Math.round((allocations.aiTrading || 0) * 31) +
      Math.round((allocations.web3 || 0) * 41)
    ) | 0
  )
    .toString(16)
    .toUpperCase()
    .padStart(6, '0')
    .slice(0, 6);

  // Attributes from allocations
  const dcaAlloc =
    allocations.dcaCrypto !== undefined
      ? allocations.dcaCrypto
      : (allocations.bitcoin || 0) + (allocations.diversified || 0);
  const aiAlloc = allocations.aiTrading || 0;
  const web3Alloc = allocations.web3 || 0;

  // Profile name translation
  const profileName = t(profileKey) || profileKey.toUpperCase();

  // Card tilt handler (3D hover)
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotY = ((x - cx) / cx) * 10; // max 10deg
    const rotX = -((y - cy) / cy) * 10;
    setTilt({ x: rotX, y: rotY, scale: 1.02 });
    setSheenPos({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0, scale: 1 });
    setSheenPos({ x: 50, y: 50 });
  };

  const captureCard = async () => {
    setTilt({ x: 0, y: 0, scale: 1 });
    await new Promise((r) => setTimeout(r, 100));
    return toPng(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#020817',
    });
  };

  const fileName = `sim360-portfolio-${profileKey}-${serial}.png`;

  const triggerDownload = (dataUrl) => {
    const link = document.createElement('a');
    link.download = fileName;
    link.href = dataUrl;
    link.click();
  };

  // Download card as PNG
  const handleDownload = async () => {
    if (!cardRef.current) return;
    try {
      setDownloading(true);
      triggerDownload(await captureCard());
    } catch (err) {
      console.error('Card download failed:', err);
      alert(t('cardDownloadError') || 'Error generating image');
    } finally {
      setDownloading(false);
    }
  };

  const buildShareMessage = () => {
    const link = BACKOFFICE_URL;
    const amount = formatCurrency(fv);
    if (currentLang === 'fr')
      return `🚀 Voici ma Carte de Portefeuille Sim360° (${profileName}) – ${amount} sur 5 ans. Et la tienne ? Crée ta carte gratuitement ici 👉 ${link}`;
    if (currentLang === 'en')
      return `🚀 This is my Sim360° Portfolio Card (${profileName}) – ${amount} in 5 years. What about yours? Create your card for free here 👉 ${link}`;
    return `🚀 Esta es mi Tarjeta de Portafolio Sim360° (${profileName}) – ${amount} en 5 años. ¿Y la tuya? Crea tu tarjeta gratis aquí 👉 ${link}`;
  };

  // Share image + message (native share on mobile, download + WhatsApp Web fallback on desktop)
  const handleShareWhatsApp = async () => {
    if (!cardRef.current) return;
    const message = buildShareMessage();
    setShareNotice(false);
    setSharing(true);
    let dataUrl;
    try {
      dataUrl = await captureCard();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: message });
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('Native share failed:', err);
    } finally {
      setSharing(false);
    }
    if (dataUrl) {
      triggerDownload(dataUrl);
      setShareNotice(true);
    }
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <Card className="bg-[#040816] border-[#0b1c38]" data-testid="portfolio-card-section">
      <CardHeader>
        <CardTitle className="text-[#00f4ff] flex items-center gap-2 text-lg">
          <Sparkles className="w-5 h-5" />
          {t('portfolioCardTitle')}
        </CardTitle>
        <p className="text-xs text-[#9EB4C0] mt-1">{t('portfolioCardSubtitle')}</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 py-4">
          {/* Card holder with 3D perspective */}
          <div
            style={{ perspective: '1200px' }}
            className="relative"
            data-testid="portfolio-card-wrapper"
          >
            <div
              ref={cardRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className="relative rounded-[22px] overflow-hidden transition-transform duration-150 ease-out select-none"
              style={{
                width: 340,
                height: 510,
                transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${tilt.scale})`,
                transformStyle: 'preserve-3d',
                boxShadow: `0 30px 80px -20px ${rarity.glow}, 0 0 0 1px rgba(255,255,255,0.05)`,
                background: rarity.frame,
                padding: 3,
              }}
            >
              {/* Inner card body */}
              <div
                className="relative w-full h-full rounded-[19px] overflow-hidden"
                style={{
                  background:
                    'radial-gradient(circle at 20% 10%, rgba(0,244,255,0.15), transparent 50%), radial-gradient(circle at 80% 90%, rgba(124,58,237,0.2), transparent 55%), linear-gradient(180deg, #0b1c38 0%, #020817 100%)',
                }}
              >
                {/* Holographic sheen (follows mouse) */}
                <div
                  className="absolute inset-0 pointer-events-none mix-blend-screen transition-opacity duration-200"
                  style={{
                    background: `radial-gradient(400px circle at ${sheenPos.x}% ${sheenPos.y}%, rgba(255,255,255,0.18), transparent 40%)`,
                    opacity: tilt.scale > 1 ? 1 : 0,
                  }}
                />
                {/* Holographic pattern */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.15] mix-blend-screen"
                  style={{
                    backgroundImage: `repeating-linear-gradient(115deg, ${rarity.tint}40 0px, transparent 6px, transparent 14px, ${rarity.tint2}30 20px)`,
                  }}
                />
                {/* Grain */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-screen"
                  style={{
                    backgroundImage:
                      'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/></svg>")',
                  }}
                />

                {/* Card content */}
                <div className="relative h-full flex flex-col p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div
                        className="text-[10px] tracking-[0.35em] font-bold"
                        style={{ color: rarity.tint }}
                      >
                        SIMULATION CARD
                      </div>
                      <div className="text-white font-black text-lg leading-none mt-1">
                        SIM360°
                      </div>
                    </div>
                    <div
                      className="text-right"
                      style={{ color: rarity.tint }}
                    >
                      <div className="text-[9px] tracking-[0.3em] font-bold opacity-70">
                        {rarity.label}
                      </div>
                      <div className="text-[10px] font-mono opacity-80">
                        #{serial}
                      </div>
                    </div>
                  </div>

                  {/* Profile name big */}
                  <div className="mt-3">
                    <div
                      className="text-[22px] font-black tracking-tight uppercase leading-none"
                      style={{
                        background: `linear-gradient(90deg, #ffffff 0%, ${rarity.tint} 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {profileName}
                    </div>
                    <div className="text-[10px] text-white/60 uppercase tracking-[0.2em] mt-1">
                      {t('portfolioCardProfile')}
                    </div>
                  </div>

                  {/* Central orb / illustration */}
                  <div className="relative flex-1 flex items-center justify-center my-2">
                    <div
                      className="absolute inset-0 rounded-xl opacity-80"
                      style={{
                        background: `radial-gradient(circle at center, ${rarity.tint}30 0%, transparent 65%)`,
                      }}
                    />
                    {/* Orbital rings */}
                    <svg
                      width="200"
                      height="200"
                      viewBox="0 0 200 200"
                      className="relative z-10"
                    >
                      <defs>
                        <radialGradient id={`orb-${profileKey}`}>
                          <stop offset="0%" stopColor={rarity.tint} stopOpacity="0.9" />
                          <stop offset="50%" stopColor={rarity.tint2} stopOpacity="0.5" />
                          <stop offset="100%" stopColor={rarity.tint} stopOpacity="0" />
                        </radialGradient>
                      </defs>
                      {/* Rings */}
                      <circle cx="100" cy="100" r="80" fill="none" stroke={rarity.tint} strokeWidth="0.5" opacity="0.3" />
                      <circle cx="100" cy="100" r="60" fill="none" stroke={rarity.tint} strokeWidth="0.5" opacity="0.5" />
                      <circle cx="100" cy="100" r="45" fill="none" stroke={rarity.tint2} strokeWidth="1" opacity="0.7" />
                      {/* Core */}
                      <circle cx="100" cy="100" r="35" fill={`url(#orb-${profileKey})`} />
                      {/* Spinning dots (3 for 3 baskets) */}
                      <g>
                        <circle cx="100" cy="20" r="3" fill="#f7931a" style={{ filter: 'drop-shadow(0 0 4px #f7931a)' }}>
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 100 100"
                            to="360 100 100"
                            dur="12s"
                            repeatCount="indefinite"
                          />
                        </circle>
                        <circle cx="180" cy="100" r="3" fill="#00f4ff" style={{ filter: 'drop-shadow(0 0 4px #00f4ff)' }}>
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 100 100"
                            to="360 100 100"
                            dur="8s"
                            repeatCount="indefinite"
                          />
                        </circle>
                        <circle cx="100" cy="180" r="3" fill="#12b7ff" style={{ filter: 'drop-shadow(0 0 4px #12b7ff)' }}>
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 100 100"
                            to="360 100 100"
                            dur="15s"
                            repeatCount="indefinite"
                          />
                        </circle>
                      </g>
                      {/* Profile emoji in the core */}
                      <text
                        x="100"
                        y="108"
                        textAnchor="middle"
                        fontSize="28"
                        fill="#fff"
                        style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.6))' }}
                      >
                        {profileKey === 'conservative'
                          ? '🛡️'
                          : profileKey === 'balanced'
                          ? '⚖️'
                          : profileKey === 'exotic'
                          ? '🚀'
                          : '✨'}
                      </text>
                    </svg>
                  </div>

                  {/* Big stats */}
                  <div className="text-center mt-1 mb-2">
                    <div
                      className="text-4xl font-black leading-none"
                      style={{
                        color: rarity.tint,
                        textShadow: `0 0 18px ${rarity.glow}`,
                      }}
                    >
                      {roi >= 0 ? '+' : ''}
                      {roi.toFixed(1)}%
                    </div>
                    <div className="text-white text-2xl font-bold mt-1">
                      {formatCurrency(fv)}
                    </div>
                    <div className="text-[10px] text-white/60 tracking-[0.2em] uppercase mt-0.5">
                      {t('portfolioCard5yProjection')}
                    </div>
                  </div>

                  {/* Attributes (like Pokémon stats) */}
                  <div className="space-y-1.5 px-1 mt-2">
                    {[
                      { label: 'DCA', value: dcaAlloc, color: '#f7931a' },
                      { label: 'AI', value: aiAlloc, color: '#00f4ff' },
                      { label: 'WEB3', value: web3Alloc, color: '#12b7ff' },
                    ].map((attr) => (
                      <div key={attr.label} className="flex items-center gap-2">
                        <span
                          className="text-[10px] font-bold w-9"
                          style={{ color: attr.color }}
                        >
                          {attr.label}
                        </span>
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${attr.value}%`,
                              background: `linear-gradient(90deg, ${attr.color} 0%, ${attr.color}80 100%)`,
                              boxShadow: `0 0 6px ${attr.color}`,
                            }}
                          />
                        </div>
                        <span className="text-[10px] text-white/70 font-mono w-9 text-right">
                          {attr.value}%
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="mt-3 flex items-center justify-between text-[9px] text-white/50 uppercase tracking-[0.2em] border-t border-white/5 pt-2">
                    <span>The DCA Club</span>
                    <span>{t('portfolioCardEducational')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions panel */}
          <div className="flex flex-col gap-3 items-stretch lg:max-w-xs w-full">
            <div className="text-sm text-[#9EB4C0] leading-relaxed">
              {t('portfolioCardDescription')}
            </div>
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-gradient-to-r from-[#0051ff] to-[#00e5ff] text-[#040816] font-bold hover:shadow-lg hover:shadow-[#00e5ff]/40 transition-all"
              data-testid="portfolio-card-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? t('portfolioCardDownloading') : t('portfolioCardDownloadPng')}
            </Button>
            <Button
              onClick={handleShareWhatsApp}
              disabled={sharing}
              variant="outline"
              className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all"
              data-testid="portfolio-card-whatsapp-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {sharing ? t('portfolioCardPreparing') : 'WhatsApp'}
            </Button>
            {shareNotice && (
              <div
                className="text-xs text-[#25D366] bg-[#25D366]/10 border border-[#25D366]/30 rounded-md p-2 leading-relaxed"
                data-testid="portfolio-card-share-notice"
              >
                {t('portfolioCardAttachNotice')}
              </div>
            )}
            <div className="text-[10px] text-[#556475] italic text-center mt-2">
              #{serial} · {rarity.label}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioCard;
